import React, { useState, useEffect } from "react";
import { getMortgageConfig } from "../utils/mortgageRatesApi";
import { authFetch } from "../api";

export default function MortgageConfigModal({ isOpen, onClose, propertyPrice, onSave, propertyId }) {
  // State for mortgage configuration
  const [downPaymentPercent, setDownPaymentPercent] = useState(25);
  const [downPaymentDollarAmount, setDownPaymentDollarAmount] = useState(null); // Store actual dollar amount entered by user
  const [mortgageTerm, setMortgageTerm] = useState("7/1 ARM");
  const [paymentType, setPaymentType] = useState("I/O");
  const [mortgageRate, setMortgageRate] = useState(7.0);

  // State for dollar input field (separate from calculated value to avoid rounding issues)
  const [dollarInputValue, setDollarInputValue] = useState("");
  const [isDollarInputFocused, setIsDollarInputFocused] = useState(false);

  // State for config info
  const [configData, setConfigData] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // State for save feedback
  const [saveStatus, setSaveStatus] = useState(null); // null | 'success'

  // Load mortgage config and saved analysis on modal open
  useEffect(() => {
    if (isOpen) {
      const loadData = async () => {
        await loadConfig();
        await loadSavedAnalysis();
      };
      loadData();
    }
  }, [isOpen, propertyId]);

  // Load config from backend (cached in memory)
  const loadConfig = async () => {
    setLoadingConfig(true);
    try {
      const response = await getMortgageConfig(propertyId);
      setConfigData(response.data);

      // Set down payment from config (will be overwritten if saved analysis has it)
      if (response.data.down_payment_percent) {
        setDownPaymentPercent(response.data.down_payment_percent);
      }

      // Auto-populate rate based on current mortgage term and payment type
      updateRateForTermAndType(mortgageTerm, paymentType, response.data);
    } catch (error) {
      console.error("Failed to load mortgage config:", error);
    } finally {
      setLoadingConfig(false);
    }
  };

  // Load saved mortgage analysis data
  const loadSavedAnalysis = async () => {
    if (!propertyId) {
      console.log('⚠️ No propertyId, skipping loadSavedAnalysis');
      return;
    }

    console.log('🔍 Loading saved mortgage analysis for property:', propertyId);

    try {
      const response = await authFetch(`/properties/${propertyId}/analysis`);
      console.log('📨 Saved analysis response:', response);

      if (response && response.analysis) {
        const analysis = response.analysis;
        console.log('✅ Found saved analysis:', analysis);

        // Load saved mortgage configuration if it exists
        if (analysis.down_payment_percent !== undefined) {
          console.log('📝 Setting down payment percent:', analysis.down_payment_percent);
          setDownPaymentPercent(analysis.down_payment_percent);
        }
        if (analysis.down_payment_amount !== undefined) {
          console.log('📝 Setting down payment dollar amount:', analysis.down_payment_amount);
          setDownPaymentDollarAmount(analysis.down_payment_amount);
        }
        if (analysis.mortgage_term) {
          console.log('📝 Setting mortgage term:', analysis.mortgage_term);
          setMortgageTerm(analysis.mortgage_term);
        }
        if (analysis.payment_type) {
          console.log('📝 Setting payment type:', analysis.payment_type);
          setPaymentType(analysis.payment_type);
        }
        if (analysis.mortgage_rate !== undefined) {
          console.log('📝 Setting mortgage rate:', analysis.mortgage_rate);
          setMortgageRate(analysis.mortgage_rate);
        }
      } else {
        console.log('⚠️ No saved analysis data');
      }
    } catch (error) {
      console.log("❌ No saved mortgage analysis found, using defaults:", error);
    }
  };

  // Update rate when mortgage term changes
  const handleMortgageTermChange = (newTerm) => {
    setMortgageTerm(newTerm);
    if (configData) {
      updateRateForTermAndType(newTerm, paymentType, configData);
    }
  };

  // Update rate when payment type changes
  const handlePaymentTypeChange = (newPaymentType) => {
    setPaymentType(newPaymentType);
    if (configData) {
      updateRateForTermAndType(mortgageTerm, newPaymentType, configData);
    }
  };

  // Map mortgage term and payment type to rate and update state
  const updateRateForTermAndType = (term, payment, config) => {
    if (!config || !config.rates) return;

    // Determine payment type key: "io" or "pi"
    const paymentKey = payment === "I/O" ? "io" : "pi";

    // Determine term key based on mortgage term
    let termKey;
    if (term === "30yr Fixed") {
      termKey = "30_year_fixed";
    } else if (term === "7/1 ARM") {
      termKey = "7_1_arm";
    } else if (term === "10/1 ARM") {
      termKey = "10_1_arm";
    }

    // Get rate from config: config.rates[paymentKey][termKey]
    if (termKey && config.rates[paymentKey] && config.rates[paymentKey][termKey]) {
      setMortgageRate(config.rates[paymentKey][termKey]);
    }
  };

  // Calculated values
  // Use the stored dollar amount if user entered it directly, otherwise calculate from percentage
  const downPaymentAmount = downPaymentDollarAmount !== null
    ? downPaymentDollarAmount
    : propertyPrice * (downPaymentPercent / 100);
  const loanAmount = propertyPrice - downPaymentAmount;

  // Calculate monthly mortgage payment
  const calculateMonthlyPayment = () => {
    if (loanAmount <= 0) return 0;

    const monthlyRate = mortgageRate / 100 / 12;

    if (paymentType === "I/O") {
      // Interest Only: just pay the interest each month
      return loanAmount * monthlyRate;
    } else {
      // Principal + Interest (Amortizing)
      let numberOfPayments;

      // For ARMs, calculate based on initial fixed period
      // But for simplicity, we'll use 30 years for all
      if (mortgageTerm === "30yr Fixed") {
        numberOfPayments = 30 * 12;
      } else if (mortgageTerm === "10/1 ARM") {
        numberOfPayments = 30 * 12; // Amortized over 30 years
      } else { // 7/1 ARM
        numberOfPayments = 30 * 12; // Amortized over 30 years
      }

      // Standard mortgage formula: M = P[r(1+r)^n]/[(1+r)^n-1]
      const monthlyPayment = loanAmount *
        (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

      return monthlyPayment;
    }
  };

  const monthlyPayment = calculateMonthlyPayment();

  // Handle save - only save, don't close
  const handleSave = () => {
    console.log('💾 handleSave called');
    console.log('💾 downPaymentDollarAmount state:', downPaymentDollarAmount);
    console.log('💾 downPaymentAmount (calculated):', downPaymentAmount);
    console.log('💾 downPaymentPercent:', downPaymentPercent);

    onSave({
      downPaymentPercent,
      downPaymentAmount,
      mortgageTerm,
      paymentType,
      mortgageRate,
      loanAmount,
      monthlyPayment,
    });

    // Show success message
    setSaveStatus('success');

    // Clear success message after 2 seconds
    setTimeout(() => {
      setSaveStatus(null);
    }, 2000);
  };

  // Handle save and close
  const handleSaveAndClose = () => {
    onSave({
      downPaymentPercent,
      downPaymentAmount,
      mortgageTerm,
      paymentType,
      mortgageRate,
      loanAmount,
      monthlyPayment,
    });
    onClose();
  };

  // Handle close without saving
  const handleClose = () => {
    setSaveStatus(null);
    onClose();
  };

  if (!isOpen) return null;

  // Styles
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  };

  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "600px",
    width: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    borderBottom: "2px solid #000",
    paddingBottom: "16px",
  };

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "700",
    color: "#333",
  };

  const closeButtonStyle = {
    fontSize: "28px",
    fontWeight: "700",
    cursor: "pointer",
    border: "none",
    background: "none",
    color: "#666",
    padding: "0",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const formGroupStyle = {
    marginBottom: "24px",
  };

  const labelStyle = {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "8px",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px",
    border: "2px solid #ddd",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
  };

  const summaryBoxStyle = {
    backgroundColor: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "24px",
    border: "2px solid #000",
  };

  const summaryRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "12px",
    fontSize: "16px",
  };

  const summaryLabelStyle = {
    color: "#666",
  };

  const summaryValueStyle = {
    fontWeight: "700",
    color: "#333",
  };

  const buttonContainerStyle = {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    marginTop: "24px",
  };

  const buttonStyle = {
    padding: "12px 24px",
    borderRadius: "6px",
    border: "none",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
  };

  const saveButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#000",
    color: "#fff",
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#f8f9fa",
    color: "#333",
    border: "2px solid #ddd",
  };

  return (
    <div style={overlayStyle} onClick={handleClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>Mortgage Configuration</h2>
          <button style={closeButtonStyle} onClick={handleClose}>×</button>
        </div>

        {/* Summary Box */}
        <div style={summaryBoxStyle}>
          <div style={summaryRowStyle}>
            <span style={summaryLabelStyle}>Property Price:</span>
            <span style={summaryValueStyle}>${propertyPrice.toLocaleString()}</span>
          </div>
          <div style={summaryRowStyle}>
            <span style={summaryLabelStyle}>Down Payment ({Number(downPaymentPercent).toFixed(2)}%):</span>
            <span style={summaryValueStyle}>${Math.round(downPaymentAmount).toLocaleString()}</span>
          </div>
          <div style={summaryRowStyle}>
            <span style={summaryLabelStyle}>Loan Amount:</span>
            <span style={summaryValueStyle}>${Math.round(loanAmount).toLocaleString()}</span>
          </div>
          <div style={{
            ...summaryRowStyle,
            marginTop: "16px",
            paddingTop: "16px",
            borderTop: "2px solid #ddd",
            fontSize: "18px",
          }}>
            <span style={summaryLabelStyle}>Monthly Payment:</span>
            <span style={{ ...summaryValueStyle, color: "#28a745" }}>
              ${Math.round(monthlyPayment).toLocaleString()}
            </span>
          </div>
        </div>

        {/* Form Fields */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Down Payment (%)</label>
            <input
              type="number"
              style={inputStyle}
              value={downPaymentPercent}
              onChange={(e) => {
                const value = e.target.value;
                console.log('📊 Down Payment (%) onChange - value:', value);
                // Clear the stored dollar amount when percentage is changed directly
                setDownPaymentDollarAmount(null);
                // Allow empty string for easier editing
                if (value === '') {
                  console.log('📊 Empty value, setting to 0');
                  setDownPaymentPercent(0);
                  return;
                }
                const percent = parseFloat(value);
                if (!isNaN(percent)) {
                  const clampedPercent = Math.max(0, Math.min(100, percent));
                  console.log('📊 Setting percent to:', clampedPercent);
                  setDownPaymentPercent(clampedPercent);
                }
              }}
              onBlur={(e) => {
                // Format to 2 decimal places on blur
                const percent = parseFloat(e.target.value) || 0;
                const clampedPercent = Math.max(0, Math.min(100, percent));
                setDownPaymentPercent(parseFloat(clampedPercent.toFixed(2)));
              }}
              min="0"
              max="100"
              step="0.01"
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Down Payment ($)</label>
            <input
              type="text"
              style={inputStyle}
              value={isDollarInputFocused ? dollarInputValue : Math.round(downPaymentAmount).toLocaleString()}
              onChange={(e) => {
                // Remove commas and parse the number
                const rawValue = e.target.value.replace(/,/g, '');
                console.log('💵 Down Payment ($) onChange - rawValue:', rawValue);

                // Store the raw input value
                setDollarInputValue(rawValue);

                // Allow empty string for easier editing
                if (rawValue === '') {
                  console.log('💵 Empty value, setting percent to 0');
                  setDownPaymentPercent(0);
                  setDownPaymentDollarAmount(null);
                  return;
                }

                const amount = parseFloat(rawValue);
                console.log('💵 Parsed amount:', amount);
                if (!isNaN(amount)) {
                  const clampedAmount = Math.max(0, Math.min(propertyPrice, amount));
                  // Store the exact dollar amount entered by user
                  setDownPaymentDollarAmount(clampedAmount);
                  const newPercent = propertyPrice > 0 ? (clampedAmount / propertyPrice) * 100 : 0;
                  const roundedPercent = parseFloat(newPercent.toFixed(2));
                  console.log('💵 Calculated percent:', newPercent, '→ Rounded:', roundedPercent);
                  console.log('💵 Storing dollar amount:', clampedAmount);
                  setDownPaymentPercent(roundedPercent);
                }
              }}
              onFocus={() => {
                console.log('💵 Input focused');
                setIsDollarInputFocused(true);
                setDollarInputValue(Math.round(downPaymentAmount).toString());
              }}
              onBlur={() => {
                console.log('💵 Input blurred');
                setIsDollarInputFocused(false);
                // If empty, reset to 0
                if (dollarInputValue === '' || dollarInputValue === '0') {
                  setDownPaymentPercent(0);
                }
              }}
            />
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Mortgage Term</label>
          <select
            style={selectStyle}
            value={mortgageTerm}
            onChange={(e) => handleMortgageTermChange(e.target.value)}
          >
            {paymentType === "P&I" && <option value="30yr Fixed">30 Year Fixed</option>}
            <option value="7/1 ARM">7/1 ARM</option>
            <option value="10/1 ARM">10/1 ARM</option>
          </select>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Payment Type</label>
          <select
            style={selectStyle}
            value={paymentType}
            onChange={(e) => handlePaymentTypeChange(e.target.value)}
          >
            <option value="I/O">Interest Only (I/O)</option>
            <option value="P&I">Principal & Interest (P&I)</option>
          </select>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Mortgage Rate (%)
          </label>
          <input
            type="number"
            style={inputStyle}
            value={mortgageRate}
            onChange={(e) => setMortgageRate(parseFloat(e.target.value) || 0)}
            min="0"
            max="20"
            step="0.125"
          />
          <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
            {loadingConfig ? "Loading config..." : "Rate auto-populated from config - adjust as needed"}
          </div>
        </div>

        {/* Save Status Message */}
        {saveStatus === 'success' && (
          <div style={{
            marginTop: "16px",
            padding: "12px",
            backgroundColor: "#d4edda",
            color: "#155724",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}>
            <span style={{ fontSize: "18px" }}>✓</span>
            <span>Saved successfully!</span>
          </div>
        )}

        {/* Action Buttons */}
        <div style={buttonContainerStyle}>
          <button style={cancelButtonStyle} onClick={handleClose}>
            Cancel
          </button>
          <button style={saveButtonStyle} onClick={handleSave}>
            Save
          </button>
          <button style={saveButtonStyle} onClick={handleSaveAndClose}>
            Save and Close
          </button>
        </div>
      </div>
    </div>
  );
}
